import { Dispatch, SetStateAction, useCallback, useState } from "react"
import { FinalSuccessDialogMeetingService } from "../../components/finalSuccessDialog/FinalSuccessDialogMeetingService"
import { ReceiptConfirmOrderDialog } from "../../../dialogs/receiptConfirmOrderDialog/ReceiptConfirmOrderDialog"
import { AccountingReferenceType } from "../../../dialogs/receiptConfirmOrderDialog/receiptConfirmOrderDialogTypes"
import {
    CreateUpdateMeetingServiceOrder,
    OrganizerSelectedValue,
    SelectedMainUserState,
    SelectedPriceListItem,
    CreateOrderDialogFlow,
    CreateOrderDialogType,
    AvailableModules,
} from "../../../createOrder/createOrderTypes"
import { createMeetingServiceOrder, updateMeetingServiceOrder } from "../../createOrderApi"
import { defaultOrderContextState, OrderContextState, OrderFormStatus } from "../../mainForm/helpers"
import { useDateTime } from "../../../../../modules/localization/useDateTime"
import { extractAccountingDimensions } from "../../components/form/helpers"
import { DynamicConfigKey, MeetingServiceTabValues, PaymentMethodOptionValue } from "../../components/form/formTypes"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { CreateOrderType } from "../../helpers"
import { DIALOG_TO_CREDIT_ACTION, makeRecurrencyICalFormat, mapToDeliveries } from "./helpers"
import { RepeatOrderFormValues } from "../drawer/meetingDetails/RepeatOrderDialog"
import { PaymentMethodType } from "../../../../../modules/payments/paymentTypes"
import { Organizer, OrganizerType } from "../../../../types/OrganizerTypes"
import { CreditNoteType } from "../../../dialogs/creditNoteDialog/helpers"

type ReceiptConfirmOrderDialogWrapProps = Readonly<{
    open: boolean
    onClose: () => void
    dialogType: CreateOrderDialogType
    orderDetails: MeetingServiceTabValues
    mainUser: SelectedMainUserState
    handleCloseMainDialog: () => void
    handleOpenResourceBooking: (type: CreateOrderType) => void
    handleAddNewMeetingService: () => void
    flowConfig?: CreateOrderDialogFlow
    orderContext: OrderContextState
    setOrderContext: Dispatch<SetStateAction<OrderContextState>>
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
    availableModules: AvailableModules
}>

export function ReceiptConfirmOrderDialogWrap({
    open,
    onClose,
    dialogType,
    orderDetails,
    mainUser,
    orderContext,
    setOrderContext,
    handleCloseMainDialog,
    handleOpenResourceBooking,
    handleAddNewMeetingService,
    flowConfig,
    orderFormStatusRef,
    availableModules,
}: ReceiptConfirmOrderDialogWrapProps) {
    const { orderId, mode } = orderContext
    const { isFullInitOrder, meetingServiceId } = orderFormStatusRef.current

    const dateTimeFactory = useDateTime()
    const [finalSuccessDialog, setFinalSuccessDialog] = useState<{
        order: CreateUpdateMeetingServiceOrder
        orderContextId?: number
    } | null>(null)
    const isSpecialDialog = dialogType !== CreateOrderDialogType.ORDER
    const creditAction = DIALOG_TO_CREDIT_ACTION[dialogType]

    const { mutateAsync: callCreateMeetingServiceOrder, isLoading: isCreating } = useApiMutation(
        createMeetingServiceOrder,
        {
            async onSuccess(params, data, _context) {
                const id = data?.order?.id
                orderFormStatusRef.current.meetingServiceId = id
                setFinalSuccessDialog({ order: params.content, orderContextId: id ?? orderId })
            },
        },
    )

    const { mutateAsync: callUpdateMeetingServiceOrder, isLoading: isUpdating } = useApiMutation(
        updateMeetingServiceOrder,
        {
            async onSuccess(params) {
                setFinalSuccessDialog({ order: params.content })
            },
        },
    )

    const isSubmitting = isCreating || isUpdating
    const hasCatering = isFullInitOrder || !!meetingServiceId

    const handleConfirm = async () => {
        if (isSubmitting) return
        const organizers: Organizer[] = [
            {
                id: mainUser.userId,
                type: OrganizerType.ORGANIZER,
            },
        ]
        const organizersValue: OrganizerSelectedValue = orderDetails[DynamicConfigKey.MEETING_RESPONSIBLE]

        if (organizersValue?.id && organizersValue.id !== mainUser.userId) {
            organizers.push({
                id: organizersValue.id,
                type: OrganizerType.RESPONSIBLE,
            })
        } else if (organizersValue?.name && organizersValue.name.trim()) {
            organizers.push({
                type: OrganizerType.RESPONSIBLE,
                name: organizersValue.name.trim(),
            })
        }

        const deliveries = mapToDeliveries(orderDetails, creditAction === CreditNoteType.CREDIT)

        const accounting = {
            dimensions: extractAccountingDimensions(orderDetails),
            ...(isSpecialDialog && orderId
                ? {
                      references: [
                          {
                              referenceType: AccountingReferenceType.ORIGINAL_ORDER,
                              orderId: orderId,
                          },
                      ],
                  }
                : {}),
        }

        const priceList: SelectedPriceListItem = orderDetails["priceList"]
        const recurrency: RepeatOrderFormValues = orderDetails[DynamicConfigKey.RECURRENCY]
        const paymentValue: PaymentMethodOptionValue = orderDetails[DynamicConfigKey.PAYMENT_METHOD]?.value

        const transformData = {
            merchant: { id: priceList.merchantId },
            webshop: {
                id: priceList.shopId,
            },
            priceList: {
                id: priceList.priceListId,
            },
            deliveries,
            meetingDetails: {
                meetingName: orderDetails[DynamicConfigKey.MEETING_NAME],
                ...(recurrency
                    ? {
                          recurrence: {
                              pattern: makeRecurrencyICalFormat(recurrency),
                          },
                      }
                    : {}),
                ...(orderDetails["event"]?.start
                    ? { startDateTime: dateTimeFactory.toApi(dateTimeFactory.fromISO(orderDetails["event"]?.start)) }
                    : {}),

                ...(orderDetails["event"]?.end
                    ? { endDateTime: dateTimeFactory.toApi(dateTimeFactory.fromISO(orderDetails["event"]?.end)) }
                    : {}),

                attendees: orderDetails[DynamicConfigKey.REGISTER_PARTICIPANTS],
                organizers,
                ...(flowConfig?.calendarAppointmentId && { calendarAppointmentId: flowConfig.calendarAppointmentId }),
            },
            payment:
                paymentValue === PaymentMethodType.INVOICE
                    ? {
                          buyerCustomerParty: {
                              id: orderDetails[DynamicConfigKey.BUYER_PARTIES].id,
                          },

                          method: PaymentMethodType.INVOICE,
                          ...(isSpecialDialog &&
                              orderId && {
                                  type: creditAction,
                              }),
                          accounting,
                      }
                    : {
                          method: PaymentMethodType.CASH,
                      },
            orderNote: orderDetails[DynamicConfigKey.MERCHANT_ORDER_NOTE],
            orderType: "MEETING_SERVICE",
        }

        if (dialogType === CreateOrderDialogType.ORDER) {
            if (orderId && hasCatering) {
                await callUpdateMeetingServiceOrder({ userId: mainUser.userId, orderId, content: transformData })
            } else {
                await callCreateMeetingServiceOrder({
                    userId: mainUser.userId,
                    content: {
                        ...transformData,
                        ...(orderId && mode !== "duplicate" ? { order: { id: orderId } } : {}),
                    },
                })
            }
        } else {
            await callCreateMeetingServiceOrder({
                userId: mainUser.userId,
                content: {
                    ...transformData,
                },
            })
        }
    }

    const handleFinalSuccessClose = useCallback(() => {
        finalSuccessDialog?.orderContextId &&
            setOrderContext((prev) => {
                return { ...prev, orderId: finalSuccessDialog.orderContextId }
            })

        setFinalSuccessDialog(null)
    }, [finalSuccessDialog?.orderContextId])

    const handleAddNewMeetingServiceClick = useCallback(() => {
        setOrderContext(defaultOrderContextState)
        handleAddNewMeetingService()
    }, [setOrderContext, handleAddNewMeetingService])

    return (
        <>
            <ReceiptConfirmOrderDialog
                open={open}
                onClose={onClose}
                handleConfirm={handleConfirm}
                dialogType={dialogType}
                orderDetails={orderDetails}
                mainUser={mainUser}
                orderId={mode !== "duplicate" && hasCatering ? orderId : undefined}
                isSubmitting={isSubmitting}
            />
            {finalSuccessDialog && (
                <FinalSuccessDialogMeetingService
                    orderDetails={finalSuccessDialog.order}
                    flowConfig={flowConfig}
                    onClose={handleFinalSuccessClose}
                    handleAddNewMeetingService={handleAddNewMeetingServiceClick}
                    handleCloseMainDialog={handleCloseMainDialog}
                    handleOpenResourceBooking={handleOpenResourceBooking}
                    orderFormStatusRef={orderFormStatusRef}
                    dialogType={dialogType}
                    availableModules={availableModules}
                />
            )}
        </>
    )
}
